/**
 * Line diff for loop definitions. Shared by the Worker and the client so a version's
 * "+12 −4 lines" reads the same everywhere. A standard longest-common-subsequence on lines,
 * with the common prefix and suffix stripped first so the quadratic part only sees the
 * region that actually changed.
 */

export type DiffKind = "same" | "added" | "removed";
export type DiffLine = { kind: DiffKind; text: string };

function splitLines(text: string): string[] {
  if (!text) return [];
  return text.replace(/\r\n?/gu, "\n").split("\n");
}

export function diffLines(before: string, after: string): DiffLine[] {
  const a = splitLines(before);
  const b = splitLines(after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }

  const lines: DiffLine[] = [];
  for (let index = 0; index < start; index += 1) lines.push({ kind: "same", text: a[index] });

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  const n = midA.length;
  const m = midB.length;
  // table[i][j] = length of the LCS of midA[i..] and midB[j..]
  const table: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i][j] = midA[i] === midB[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (midA[i] === midB[j]) {
      lines.push({ kind: "same", text: midA[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      // On a tie, show the removal first so a changed line reads "old, then new".
      lines.push({ kind: "removed", text: midA[i] });
      i += 1;
    } else {
      lines.push({ kind: "added", text: midB[j] });
      j += 1;
    }
  }
  while (i < n) lines.push({ kind: "removed", text: midA[i++] });
  while (j < m) lines.push({ kind: "added", text: midB[j++] });

  for (let index = endA; index < a.length; index += 1) lines.push({ kind: "same", text: a[index] });
  return lines;
}

export function countDiff(diff: DiffLine[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const line of diff) {
    if (line.kind === "added") added += 1;
    else if (line.kind === "removed") removed += 1;
  }
  return { added, removed };
}

/** "+12 −4 lines"; "No line changes" when nothing moved. */
export function summarizeDiff(diff: DiffLine[]): string {
  const { added, removed } = countDiff(diff);
  if (added === 0 && removed === 0) return "No line changes";
  return `+${added} −${removed} ${added + removed === 1 ? "line" : "lines"}`;
}
